interface ApiTestResult {
  route: string;
  name: string;
  available: boolean;
  responseTime?: number;
  error?: string;
  dataSource: 'api' | 'mock';
}

class ApiTestService {
  private readonly API_BASE_URL = 'http://localhost:3001';
  private readonly API_ROUTES = [
    // Rotas principais da API
    { route: '/api/users', name: 'Users' },
    { route: '/api/accounts', name: 'Accounts' },
    { route: '/api/roles', name: 'Roles' },
    { route: '/api/auth/login', name: 'Auth Login' },
    { route: '/api/auth/register', name: 'Auth Register' },
    { route: '/api/auth/profile', name: 'Auth Profile' },
    { route: '/api/analytics', name: 'Analytics' },
    { route: '/api/ads', name: 'Ads' },
    { route: '/api/instagram', name: 'Instagram' },
    
    // Rotas de gerenciamento (podem precisar de autenticação)
    { route: '/users', name: 'Users Management' },
    { route: '/auth', name: 'Auth Management' },
    { route: '/api/accounts/1', name: 'Account Details' },
    { route: '/api/users/1', name: 'User Details' },
    { route: '/api/roles/1', name: 'Role Details' },
    
    // Rotas de documentação
    { route: '/api', name: 'API Docs' },
    { route: '/docs', name: 'Documentation' },
    { route: '/swagger', name: 'Swagger UI' },
    
    // Rota de teste
    { route: '/test', name: 'Test Endpoint' },
  ];

  async testAllApis(): Promise<ApiTestResult[]> {
    const results: ApiTestResult[] = [];

    for (const apiRoute of this.API_ROUTES) {
      const result = await this.testApi(apiRoute.route, apiRoute.name);
      results.push(result);
    }

    return results;
  }

  private async testApi(route: string, name: string): Promise<ApiTestResult> {
    const startTime = Date.now();
    
    try {
      const response = await fetch(`${this.API_BASE_URL}${route}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // Timeout de 5 segundos para evitar esperar muito tempo
        signal: AbortSignal.timeout(5000)
      });

      const responseTime = Date.now() - startTime;

      // Considerar como disponível se retornar 200, 401 (precisa auth) ou 403 (forbidden)
      if (response.ok || response.status === 401 || response.status === 403) {
        return {
          route,
          name,
          available: true,
          responseTime,
          dataSource: 'api'
        };
      } else {
        return {
          route,
          name,
          available: false,
          responseTime,
          error: 'Mock',
          dataSource: 'mock'
        };
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      // Se for erro de timeout ou conexão, considerar como não disponível
      if (error instanceof Error) {
        if (error.name === 'AbortError' || error.message.includes('fetch')) {
          return {
            route,
            name,
            available: false,
            responseTime,
            error: 'Mock',
            dataSource: 'mock'
          };
        }
      }
      
      return {
        route,
        name,
        available: false,
        responseTime,
        error: 'Mock',
        dataSource: 'mock'
      };
    }
  }

  async testSpecificApi(route: string, name: string): Promise<ApiTestResult> {
    return this.testApi(route, name);
  }

  // Método para testar apenas as rotas principais (mais rápidas)
  async testMainApis(): Promise<ApiTestResult[]> {
    const mainRoutes = this.API_ROUTES.slice(0, 8); // Primeiras 8 rotas
    const results: ApiTestResult[] = [];

    for (const apiRoute of mainRoutes) {
      const result = await this.testApi(apiRoute.route, apiRoute.name);
      results.push(result);
    }

    return results;
  }
}

export const apiTestService = new ApiTestService();
export type { ApiTestResult }; 